<script setup lang="ts">
import { Line } from 'vue-chartjs';
import {
    Chart as ChartJS, LineElement, LinearScale, CategoryScale, PointElement, Filler,
} from 'chart.js';
import { computed } from 'vue';
import type { ChartData, ChartOptions } from 'chart.js';

type PropsType = {
    data: number[];
    color?: string;
    height?: number;
    fill?: boolean;
    tension?: number;
    showLastDot?: boolean;
};

const props = withDefaults(defineProps<PropsType>(), {
    color: 'primary',
    height: 40,
    fill: true,
    tension: 0.4,
    showLastDot: false,
});

ChartJS.register(LineElement, LinearScale, CategoryScale, PointElement, Filler);

defineOptions({ inheritAttrs: false });

const themeColors: Record<string, string> = {
    primary: '#62C5C7',
    secondary: '#555555',
    success: '#1EA966',
    warning: '#B18344',
    error: '#C0191E',
    info: '#034692',
    accent: '#F14172',
    'accent-mint': '#5fd4c4',
    'accent-purple': '#8b5cf6',
    'accent-orange': '#f59e0b',
    'accent-pink': '#ec4899',
    'accent-blue-dashboard': '#3b82f6',
    'accent-green-dashboard': '#10b981',
    'accent-red-dashboard': '#ef4444',
};

const getThemeColor = (colorName: string): string => {
    return themeColors[colorName] || themeColors['primary'];
};

const hexToRgba = (hex: string, alpha: number): string => {
    const r = Number.parseInt(hex.slice(1, 3), 16);
    const g = Number.parseInt(hex.slice(3, 5), 16);
    const b = Number.parseInt(hex.slice(5, 7), 16);

    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const chartData = computed((): ChartData<'line'> => {
    const colorValue = getThemeColor(props.color);
    const fillColorValue = hexToRgba(colorValue, 0.15);

    return {
        labels: props.data.map((_, index) => index.toString()),
        datasets: [
            {
                data: props.data,
                borderColor: colorValue,
                backgroundColor: props.fill ? fillColorValue : 'transparent',
                borderWidth: 2,
                fill: props.fill,
                tension: props.tension,
                pointRadius: props.showLastDot
                    ? props.data.map((_, index) => (index === props.data.length - 1 ? 3 : 0))
                    : 0,
                pointHoverRadius: 0,
                pointBackgroundColor: colorValue,
                pointBorderColor: colorValue,
            },
        ],
    };
});

const chartOptions = computed(
    (): ChartOptions<'line'> => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: { enabled: false },
        },
        scales: {
            x: { display: false },
            y: { display: false },
        },
        elements: {
            point: { hoverRadius: 0 },
            line: { borderWidth: 2 },
        },
        interaction: {
            mode: 'nearest',
            intersect: false,
        },
    }),
);

const containerStyle = computed(() => ({
    height: `${props.height}px`,
    width: '100%',
}));

const hasData = computed(() => props.data.length > 0);
</script>

<template>
    <div
        v-if="hasData"
        class="b-sparkline"
        :style="containerStyle"
        v-bind="$attrs"
    >
        <Line
            :data="chartData"
            :options="chartOptions"
        />
    </div>
</template>

<style scoped lang="scss">
.b-sparkline {
    position: relative;
    display: inline-block;
}
</style>
